import Alert from "common/components/ui/Alert";
import Switch from "common/components/ui/Switch";
import useCommunicationSettings from "common/datahooks/useCommunicationSettings";
import usePushService from "common/hooks/usePushService";
import type { CommunicationSettings } from "common/types";

import SettingsItemContainer from "../SettingsItemContainer";

const allSettings: {
  key: keyof CommunicationSettings;
  title: string;
  subtitle: string;
}[] = [
  {
    key: "email",
    title: "Email Communication",
    subtitle:
      "Promotions, Discounts, Service Updates and the Aimfox Newsletter",
  },
  {
    key: "phone",
    title: "SMS Communication",
    subtitle: "High impact promotions and important updates",
  },
  {
    key: "webpush",
    title: "Web Push Communication",
    subtitle:
      "Get campaign, account and service updates directly in your browser",
  },
];

export default function Communication() {
  const {
    communicationSettings,
    isLoadingCommunicationSettings,
    updateCommunicationSettings,
  } = useCommunicationSettings();
  const { updateLocalStorage, unsubscribeWebPush } = usePushService();

  const shouldShowCommunicationShadow =
    communicationSettings && !isLoadingCommunicationSettings;

  const handleToggle = async (key: keyof CommunicationSettings) => {
    const isSettingEnabled = !communicationSettings[key];
    try {
      // Update settings
      await updateCommunicationSettings({
        ...communicationSettings,
        [key]: isSettingEnabled,
      });

      // Additional handling for Web Push setting
      if (key === "webpush") {
        if (isSettingEnabled) {
          unsubscribeWebPush();
        } else {
          updateLocalStorage(false);
        }
      }
    } catch (_err) {
      // Revert to original state if error happens
      updateCommunicationSettings({
        ...communicationSettings,
        [key]: communicationSettings[key],
      });
    }
  };

  return (
    <SettingsItemContainer className="flex-col lg:flex-row gap-1 lg:gap-16">
      <span className="lg:text-sm-bold text-sm-medium max-lg:text-black-500">
        Communication
      </span>
      <div className="flex flex-col flex-1">
        {shouldShowCommunicationShadow ? (
          <div className="flex flex-col gap-6">
            {allSettings.map(({ key, title, subtitle }) => {
              const isEnabled = communicationSettings[key];

              return (
                <div
                  className="flex flex-row items-center justify-between gap-4"
                  key={key}
                >
                  <div className="flex flex-col gap-y-0.5">
                    <span className="text-sm-bold text-black-950">{title}</span>
                    <p className="text-xs-medium text-black-500">{subtitle}</p>
                  </div>

                  <Switch
                    aria-label={`${isEnabled ? "turn off" : "turn on"} ${title}`}
                    id={key}
                    size="md"
                    checked={isEnabled}
                    onClick={() => handleToggle(key)}
                  />
                </div>
              );
            })}
            <Alert variant="warning">
              We will always send you transactional and service critical updates
            </Alert>
          </div>
        ) : (
          <div />
        )}
      </div>
    </SettingsItemContainer>
  );
}
